<template>
  <section>
    <summary-block
      :properties="financingSummary"
      :is-loading="financingIsLoading"
      data-qa="financing-block"
      @show-in-pdf="changeShowInPdf"
      @show-config-modal="showConfigModal"
    />

    <modal
      v-if="showFinancialServiceConfig"
      id="financing"
      :title="retrieveModalText('title')"
      @close="showConfigModal(false)"
    >
      <financing-form
        :financial-params="computedFinancialParams"
        :product-types="productTypes"
        :object-type="objectType"
        :bpm-code="bpmCode"
        :dictionary-folder="productTypeDictionaryFolder"
        :is-loading="financingIsLoading"
        :is-read-only="isReadOnly"
        :vwpfs-silva-enabled="dealerSettings && dealerSettings.vwpfsSilvaEnabled"
        @update-financing="handleUpdateFinancing"
        @request-silva="requestSilva"
        @submit="showConfigModal(false)"
      />
    </modal>

    <confirmation-prompt
      v-if="dealerSettings && dealerSettings.vwpfsSilvaEnabled"
      id="silvaPrompt"
      :title="retrieveModalText('silvaprompt/title')"
      :show="showRequestSilvaPrompt"
      :info-text="retrieveModalText('silvaprompt/confirmationtext')"
      :align-buttons="'center'"
      :cancel-button-show="false"
      :confirm-button-text="retrieveModalText('silvaprompt/confirmbuttontext')"
      @confirm="openSilvaLink()"
      @close="openSilvaPrompt(false)"
    >
    </confirmation-prompt>
  </section>
</template>

<script>
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import ConfirmationPrompt from '@/components/generic/presentational/molecules/confirmationPrompt/ConfirmationPrompt.vue'
import FinancingForm from '@/components/generic/container/FinancialServices/Financing/FinancingForm.vue'
import SummaryBlock from '@/components/generic/container/FinancialServices/SummaryBlock.vue'
import FinancialServices from '@/api/server/FinancialServices'
import { mapGetters } from 'vuex'
import { getBrandName, brands } from '@/helpers/BrandHelper'
import FinancialServiceBlock from '@/mixins/financialServices/financialServiceBlock'

export default {
  components: {
    Modal,
    SummaryBlock,
    FinancingForm,
    ConfirmationPrompt,
  },
  mixins: [FinancialServiceBlock],
  provide() {
    return {
      // Properties
      channels: this.channels,
      financingProductType: this.financingProductType,
    }
  },
  data() {
    return {
      // Properties from modal
      financialParams: {
        productType: '',
        channelindex: 0,
        downPayment: {
          value: 0,
          min: 0,
          max: 0,
        },
        balloonPayment: {
          value: 0,
          min: 0,
          max: 0,
        },
        duration: {
          value: 0,
          min: 0,
          max: 100,
          step: 1,
        },
        interestRate: 0,
        effectiveAnnualInterestRate: 0,
        netCreditAmount: 0,
        grossCreditAmount: 0,
        ratePerMonth: null,
        vatAmount: 0,
        errors: null,
      },
      showRequestSilvaPrompt: false,
      silvaLink: null,
    }
  },
  computed: {
    ...mapGetters({
      isStatusSalesOrder: 'quote/quoteStatusIsSalesOrder',
      quoteShowInPdf: 'quote/showVwpfsFinancePdf',
      showVwpfsFinancePdf: 'quote/showVwpfsFinancePdf',
      showVwpfsPrivateLeasePdf: 'quote/showVwpfsPrivateLeasePdf',
      showVwpfsInsurancePdf: 'quote/showVwpfsInsurancePdf',
      showVwpfsOperationalLeasePdf: 'quote/showVwpfsOperationalLeasePdf',
      contractor: 'quote/printOptions/contractor',
      contactInfoArray: 'quote/printOptions/contactInfo',
    }),
    financingProductType() {
      return Object.freeze({
        financialLease: 'FL',
        hirePurchase: 'HK',
      })
    },
    financingSummary() {
      return {
        title: this.retrieveText('sectiontitle'),
        summaryLine: this.retrieveText('summaryline', {
          downPayment: this.i18n.n(this.financialParams.downPayment.value, 'currencyRounded'),
          balloonPayment: this.i18n.n(this.financialParams.balloonPayment.value, 'currencyRounded'),
          duration: this.financialParams.duration.value,
        }),
        rateTotal: this.financialParams.ratePerMonth
          ? this.i18n.n(this.financialParams.ratePerMonth, 'currencyRounded')
          : '-',
        infoModalTitle: this.retrieveText('summaryinfotitle'),
        infoModalContent: this.retrieveInfoText('summaryinfotext'),
        showInPdfCheckboxLabel: this.retrieveText('showVwpfsFinancePdf Label'),
        openModalButtonLabel: this.retrieveText('openmodalbutton'),
        isReadOnly: this.isReadOnly,
        showInPdf: this.showInPdf,
      }
    },

    channels() {
      return this.dealerSettings?.supportedVwpfsChannels?.map((channel) => channel.channel)
    },
    productTypes() {
      return [
        {
          name: this.retrieveModalText('hirepurchaselabel'),
          value: this.financingProductType.hirePurchase,
        },
        {
          name: this.retrieveModalText('financialleaselabel'),
          value: this.financingProductType.financialLease,
        },
      ]
    },
    bpmCode() {
      return this.configuration?.BpmCode
    },
    objectType() {
      if (this.isHirePurchase) {
        return 'PG'
      }
      switch (this.bpmCode) {
        case 'PD':
        case 'PB':
        case 'PE':
        case 'PH':
          return 'ZG'
        case 'BF':
        case 'BE':
          return 'GRZ'
        case 'CF':
        case 'CC':
          return 'GRL'
      }

      //N*,NE,ND,NB,NH,NF
      if (this.bpmCode?.startsWith('N')) return 'GRL'

      return 'ZG'
    },
    computedFinancialParams() {
      return {
        ...this.financialParams,
        vehiclePriceIncl: this.quote?.Metadata?.Prices?.TotalPrice?.Incl,
        vehiclePriceExcl: this.quote?.Metadata?.Prices?.TotalPrice?.ExclBTWinclBPM,
      }
    },
    // Helper properties
    isHirePurchase() {
      return this.financialParams.productType === this.financingProductType.hirePurchase
    },
    productTypeDictionaryFolder() {
      switch (this.financialParams.productType) {
        case this.financingProductType.financialLease:
          return 'financiallease'
        case this.financingProductType.hirePurchase:
        default:
          return 'hirepurchase'
      }
    },
  },
  created() {
    // Initialise data props
    let defChannelIndex = this.dealerSettings?.supportedVwpfsChannels?.findIndex(
      (channel) => channel.default
    )
    if (defChannelIndex === -1) defChannelIndex = 0
    this.financialParams.channelIndex = defChannelIndex
    this.financialParams.productType =
      getBrandName() === brands.VwBedrijfswagens
        ? this.financingProductType.financialLease
        : this.productTypes[0]?.value
  },
  methods: {
    async requestFinancing(initial = false) {
      this.setFinancingIsLoading(true)

      const request = {
        quoteId: this.quote?.QuoteId,
        objectType: this.objectType,
        productType: this.financialParams.productType,
        objectPriceExVATExBPM: parseFloat(this.computedFinancialParams.vehiclePriceExcl),
        objectPriceInclVATInclBPM: parseFloat(this.computedFinancialParams.vehiclePriceIncl),
        objectPriceVAT: parseFloat(this.quote?.Metadata?.Prices?.TotalPrice?.Btw),
        channel: this.channels[this.financialParams.channelIndex],
      }

      if (!initial) {
        request.downPayment = parseFloat(this.financialParams.downPayment.value)
        request.balloonPayment = parseFloat(this.financialParams.balloonPayment.value)
        request.duration = parseInt(this.financialParams.duration.value)
      }

      const response = await FinancialServices.setFinancing(request).catch((e) => {
        this.setFinancingIsLoading(false)
        return null
      })

      if (!response) {
        return
      }

      this.financialParams.downPayment.value = response.calculationParameters.downPayment
      this.financialParams.downPayment.min = response.calculationParameters.downPaymentMinAmount
      this.financialParams.downPayment.max = response.calculationParameters.downPaymentMaxAmount

      this.financialParams.balloonPayment.value = response.calculationParameters.balloonPayment
      this.financialParams.balloonPayment.min =
        response.calculationParameters.balloonPaymentMinAmount
      this.financialParams.balloonPayment.max =
        response.calculationParameters.balloonPaymentMaxAmount

      this.financialParams.duration.value = response.calculationParameters.duration
      this.financialParams.duration.min = response.calculationParameters.durationMin
      this.financialParams.duration.max = response.calculationParameters.durationMax
      this.financialParams.duration.step = response.calculationParameters.durationStep

      if (!response.result) {
        response.result = {
          interestRate: 0,
          effectiveAnnualInterestRate: 0,
          netCreditAmount: 0,
          grossCreditAmount: 0,
          ratePerMonth: null,
          finalRate: null,
        }
      }

      this.financialParams.interestRate = response.result.interestRate
      this.financialParams.effectiveAnnualInterestRate = response.result.effectiveAnnualInterestRate
      this.financialParams.netCreditAmount = response.result.netCreditAmount
      this.financialParams.grossCreditAmount = response.result.grossCreditAmount
      this.financialParams.ratePerMonth = response.errors ? null : response.result.ratePerMonth
      this.financialParams.errors = response.errors
        ? Object.keys(response.errors).map((key) => response.errors[key])
        : null
      this.financialParams.vatAmount = request.objectPriceVAT
      this.$store.dispatch('quote/setLatestVwpfsCall', response)

      this.setFinancingIsLoading(false)
    },
    async initFinancing() {
      this.showInPdf = this.quoteShowInPdf
      if (!!this.quote.LatestVwpfsCall) {
        this.setFinancingIsLoading(true)

        const latestVwpfsCallData = this.quote.LatestVwpfsCall

        this.financialParams.downPayment.value = latestVwpfsCallData.DownPayment
        this.financialParams.balloonPayment.value = latestVwpfsCallData.BalloonPayment
        this.financialParams.duration.value = latestVwpfsCallData.Duration
        this.financialParams.productType = latestVwpfsCallData.ProductType

        if (this.financialParams.productType === 'null') this.financialParams.productType = null

        const channelIndex = this.channels.indexOf(latestVwpfsCallData.Channel)
        this.financialParams.channelIndex =
          channelIndex > -1 ? channelIndex : this.financialParams.channelindex

        if (!this.isReadOnly) {
          this.requestFinancing()
        } else {
          this.financialParams.interestRate = latestVwpfsCallData.InterestRateNominal
          this.financialParams.effectiveAnnualInterestRate =
            latestVwpfsCallData.InterestRateEffective
          this.financialParams.netCreditAmount = latestVwpfsCallData.NetCreditAmount
          this.financialParams.grossCreditAmount = latestVwpfsCallData.GrossCreditAmount
          this.financialParams.ratePerMonth = latestVwpfsCallData.errors
            ? null
            : latestVwpfsCallData.MonthPrice
          this.financialParams.vatAmount = parseFloat(this.quote?.Metadata?.Prices?.TotalPrice?.Btw)
        }
        this.setFinancingIsLoading(false)
      } else {
        this.requestFinancing(true)
      }
      // Call financing after any price change
      this.$watch('computedFinancialParams.vehiclePriceExcl', () => this.requestFinancing())
    },
    async changeShowInPdf(showInPdf) {
      await this.$store.dispatch('quote/setShowVwpfsFinancePdf', showInPdf)

      const pdfOptions = {
        quoteId: this.quote?.QuoteId,
        printContractorOnOrder: this.contractor,
        printContactInfoOnPdf: this.contactInfoArray,
        showVwpfsFinancePdf: this.showVwpfsFinancePdf,
        showVwpfsPrivateLeasePdf: this.showVwpfsPrivateLeasePdf,
        showVwpfsInsurancePdf: this.showVwpfsInsurancePdf,
        showVwpfsOperationalLeasePdf: this.showVwpfsOperationalLeasePdf,
      }

      await this.$store.dispatch('quote/savePrintOptions', pdfOptions)
    },
    retrieveText(labelName, params = null) {
      return this.i18n.t(`/financialservices/financing/${labelName}`, params)
    },
    openSilvaPrompt(open) {
      this.showRequestSilvaPrompt = open
      if (!open) {
        this.silvaLink = null
      }
    },
    requestSilva() {
      const request = {
        quoteId: this.quote.QuoteId,
      }
      this.setFinancingIsLoading(true)
      FinancialServices.requestSilva(request)
        .then((response) => {
          this.silvaLink = response.Url
          this.openSilvaPrompt(true)

          this.setFinancingIsLoading(false)
        })
        .catch(() => {
          this.setFinancingIsLoading(false)
        })
    },
    openSilvaLink() {
      window.open(this.silvaLink)
      this.openSilvaPrompt(false)
    },
  },
}
</script>
